/**
 * Storage Manager - Cloud-first data persistence
 * Data is read from Firebase and cached locally for display
 * Writes still go to cloud via sync queue
 */

import { generateUUID } from './uuid.js'

// Cloud-first mode: races and athletes come from Firebase subscriptions
// This in-memory cache is populated by Firebase subscriptions
let cloudRacesCache = []
let cloudAthletesCache = {} // keyed by raceId

/**
 * Set the cloud races cache (called from Firebase subscription)
 */
export function setCloudRaces(races) {
  cloudRacesCache = races
  // Also save to localStorage as backup for offline
  saveLocal(STORAGE_KEYS.RACES, races)
}

/**
 * Set the cloud athletes cache for a race (called from Firebase subscription)
 */
export function setCloudAthletes(raceId, athletes) {
  cloudAthletesCache[raceId] = athletes
  // Also save to localStorage as backup
  const rosters = loadLocal(STORAGE_KEYS.ROSTERS) || {}
  rosters[raceId] = athletes
  saveLocal(STORAGE_KEYS.ROSTERS, rosters)
}

/**
 * Get races from cloud cache (falls back to localStorage if cache empty)
 */
export function getCloudRaces() {
  if (cloudRacesCache.length > 0) {
    return [...cloudRacesCache]
  }
  // Fallback to localStorage (for initial load before subscription fires)
  return loadLocal(STORAGE_KEYS.RACES) || []
}

/**
 * Add a race to the cloud cache immediately (optimistic update)
 * Called when creating a race locally before Firebase sync completes
 */
export function addToCloudRacesCache(race) {
  // Check if race already exists in cache
  const existingIndex = cloudRacesCache.findIndex(r => r.id === race.id)
  if (existingIndex >= 0) {
    cloudRacesCache[existingIndex] = race
  } else {
    cloudRacesCache.push(race)
  }
}

/**
 * Get athletes from cloud cache for a race
 */
export function getCloudAthletes(raceId) {
  if (cloudAthletesCache[raceId]) {
    return cloudAthletesCache[raceId]
  }
  // Fallback to localStorage
  const rosters = loadLocal(STORAGE_KEYS.ROSTERS) || {}
  return rosters[raceId] || []
}

const STORAGE_KEYS = {
  TIMESTAMPS: 'manatiming_timestamps',
  BIBS: 'manatiming_bibs',
  ROSTER: 'manatiming_roster',
  ROSTERS: 'manatiming_rosters', // Race-specific rosters keyed by race ID
  RACE: 'manatiming_race',
  RACES: 'manatiming_races',
  SELECTED_RACE: 'manatiming_selected_race',
  SYNC_QUEUE: 'manatiming_sync_queue',
  CSV_MAPPINGS: 'manatiming_csv_mappings' // Saved CSV column mappings
}

/**
 * Get race-specific storage key
 * @param {string} baseKey - Base storage key
 * @param {string} raceId - Race ID
 * @returns {string} Combined key
 */
export function getRaceKey(baseKey, raceId) {
  if (!raceId) return baseKey
  return `${baseKey}_${raceId}`
}

// Age group preset definitions
const AGE_GROUP_PRESETS = {
  standard: {
    name: 'Standard 5-Year Brackets',
    groups: [
      { name: '14 & Under', gender: 'All', minAge: null, maxAge: 14, sortOrder: 0 },
      { name: '15-19', gender: 'All', minAge: 15, maxAge: 19, sortOrder: 1 },
      { name: '20-29', gender: 'All', minAge: 20, maxAge: 29, sortOrder: 2 },
      { name: '30-39', gender: 'All', minAge: 30, maxAge: 39, sortOrder: 3 },
      { name: '40-49', gender: 'All', minAge: 40, maxAge: 49, sortOrder: 4 },
      { name: '50-59', gender: 'All', minAge: 50, maxAge: 59, sortOrder: 5 },
      { name: '60-69', gender: 'All', minAge: 60, maxAge: 69, sortOrder: 6 },
      { name: '70+', gender: 'All', minAge: 70, maxAge: null, sortOrder: 7 }
    ]
  },
  foot_forward: {
    name: 'Foot Forward 5K',
    groups: [
      { name: 'Under 18', gender: 'All', minAge: null, maxAge: 17, sortOrder: 0 },
      { name: '19-30', gender: 'All', minAge: 18, maxAge: 30, sortOrder: 1 },
      { name: '31-45', gender: 'All', minAge: 31, maxAge: 45, sortOrder: 2 },
      { name: '46-60', gender: 'All', minAge: 46, maxAge: 60, sortOrder: 3 },
      { name: '60+', gender: 'All', minAge: 61, maxAge: null, sortOrder: 4 }
    ]
  },
  high_school: {
    name: 'High School XC',
    groups: [
      { name: 'Freshman', gender: 'All', minAge: null, maxAge: null, grade: 9, sortOrder: 0 },
      { name: 'Sophomore', gender: 'All', minAge: null, maxAge: null, grade: 10, sortOrder: 1 },
      { name: 'Junior', gender: 'All', minAge: null, maxAge: null, grade: 11, sortOrder: 2 },
      { name: 'Senior', gender: 'All', minAge: null, maxAge: null, grade: 12, sortOrder: 3 },
      { name: 'Open', gender: 'All', minAge: null, maxAge: null, sortOrder: 4 }
    ]
  },
  youth: {
    name: 'Youth Running',
    groups: [
      { name: '8 & Under', gender: 'All', minAge: null, maxAge: 8, sortOrder: 0 },
      { name: '9-10', gender: 'All', minAge: 9, maxAge: 10, sortOrder: 1 },
      { name: '11-12', gender: 'All', minAge: 11, maxAge: 12, sortOrder: 2 },
      { name: '13-14', gender: 'All', minAge: 13, maxAge: 14, sortOrder: 3 },
      { name: '15-18', gender: 'All', minAge: 15, maxAge: 18, sortOrder: 4 }
    ]
  },
  none: {
    name: 'No Age Groups (Overall Only)',
    groups: []
  },
  custom: {
    name: 'Custom',
    groups: []
  }
}

/**
 * Save data to localStorage with timestamp
 */
export function saveLocal(key, data) {
  try {
    const payload = {
      data,
      savedAt: Date.now(),
      version: 1
    }
    localStorage.setItem(key, JSON.stringify(payload))
    return true
  } catch (e) {
    console.error('LocalStorage save failed:', e)
    return false
  }
}

/**
 * Load data from localStorage
 */
export function loadLocal(key) {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const payload = JSON.parse(raw)
    return payload.data
  } catch (e) {
    console.error('LocalStorage load failed:', e)
    return null
  }
}

/**
 * Add item to sync queue for background sync
 */
export function queueForSync(type, data) {
  const queue = loadLocal(STORAGE_KEYS.SYNC_QUEUE) || []
  queue.push({
    type,
    data,
    queuedAt: Date.now(),
    id: generateUUID()
  })
  saveLocal(STORAGE_KEYS.SYNC_QUEUE, queue)
}

/**
 * Get pending sync items
 */
export function getSyncQueue() {
  return loadLocal(STORAGE_KEYS.SYNC_QUEUE) || []
}

/**
 * Remove item from sync queue after successful sync
 */
export function removeFromSyncQueue(id) {
  const queue = loadLocal(STORAGE_KEYS.SYNC_QUEUE) || []
  const filtered = queue.filter(item => item.id !== id)
  saveLocal(STORAGE_KEYS.SYNC_QUEUE, filtered)
}

/**
 * Clear all local data (use with caution)
 */
export function clearAllLocal() {
  Object.values(STORAGE_KEYS).forEach(key => {
    localStorage.removeItem(key)
  })
}

/**
 * Clear all data - races, rosters, timestamps, bibs
 * Used for pre-race cleanup
 * @returns {Array} List of race IDs that were deleted (for Firebase cleanup)
 */
export function clearAllData() {
  const races = getRaces()
  const raceIds = races.map(r => r.id)

  // Queue each race for Firebase deletion (this cascades to timestamps, bibs, athletes)
  raceIds.forEach(raceId => {
    queueForSync('race_delete', { id: raceId })
  })

  // Clear all localStorage
  Object.values(STORAGE_KEYS).forEach(key => {
    localStorage.removeItem(key)
  })

  // Also clear any race-specific keys
  const keysToRemove = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key && key.startsWith('manatiming_')) {
      keysToRemove.push(key)
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key))

  return raceIds
}

// ============================================
// Roster-specific functions
// ============================================

/**
 * Save roster for a specific race
 * @param {Array} athletes - Array of athlete objects
 * @param {string} raceId - Race ID (optional, uses selected race if not provided)
 */
export function saveRoster(athletes, raceId = null) {
  const targetRaceId = raceId || getSelectedRace()?.id

  if (targetRaceId) {
    // Save to race-specific roster
    const rosters = loadLocal(STORAGE_KEYS.ROSTERS) || {}
    rosters[targetRaceId] = athletes
    saveLocal(STORAGE_KEYS.ROSTERS, rosters)

    // Optimistic update: add to cloud cache immediately
    setCloudAthletes(targetRaceId, athletes)
  }

  // Also save to legacy roster key for backwards compatibility
  saveLocal(STORAGE_KEYS.ROSTER, athletes)
  queueForSync('roster_upload', { athletes, count: athletes.length, raceId: targetRaceId })
}

/**
 * Load roster for a specific race - CLOUD FIRST
 * Returns athletes from cloud cache, falls back to localStorage
 * @param {string} raceId - Race ID (optional, uses selected race if not provided)
 * @returns {Array} Array of athlete objects
 */
export function loadRoster(raceId = null) {
  const targetRaceId = raceId || getSelectedRace()?.id

  if (targetRaceId) {
    // Cloud-first: prefer cloud cache
    const cloudAthletes = getCloudAthletes(targetRaceId)
    if (cloudAthletes.length > 0) {
      return cloudAthletes
    }

    // Fallback to localStorage
    const rosters = loadLocal(STORAGE_KEYS.ROSTERS) || {}
    if (rosters[targetRaceId]) {
      return rosters[targetRaceId]
    }
  }

  // Fallback to legacy roster
  return loadLocal(STORAGE_KEYS.ROSTER) || []
}

/**
 * Clear roster for a specific race
 * @param {string} raceId - Race ID (optional, uses selected race if not provided)
 */
export function clearRoster(raceId = null) {
  const targetRaceId = raceId || getSelectedRace()?.id

  if (targetRaceId) {
    const rosters = loadLocal(STORAGE_KEYS.ROSTERS) || {}
    delete rosters[targetRaceId]
    saveLocal(STORAGE_KEYS.ROSTERS, rosters)
  }

  // Also clear legacy roster
  localStorage.removeItem(STORAGE_KEYS.ROSTER)
}

/**
 * Look up athlete by bib number
 * Searches ALL races (not just selected race) to support runners in multiple events
 * @param {number} bib - Bib number
 * @param {string} raceId - Race ID (optional, searches this race first, then all others)
 * @returns {Object|null} Athlete object or null
 */
export function getAthleteByBib(bib, raceId = null) {
  if (!bib) return null
  const bibStr = String(bib)

  // First try the specified/selected race
  const targetRaceId = raceId || getSelectedRace()?.id
  if (targetRaceId) {
    const roster = loadRoster(targetRaceId)
    const found = roster.find(a => String(a.bib) === bibStr)
    if (found) return found
  }

  // If not found, search all races in the cloud cache
  for (const cacheRaceId in cloudAthletesCache) {
    if (cacheRaceId === targetRaceId) continue // Already checked
    const athletes = cloudAthletesCache[cacheRaceId]
    const found = athletes.find(a => String(a.bib) === bibStr)
    if (found) return found
  }

  // Also check localStorage rosters as fallback
  const rosters = loadLocal(STORAGE_KEYS.ROSTERS) || {}
  for (const storedRaceId in rosters) {
    if (storedRaceId === targetRaceId) continue // Already checked
    const athletes = rosters[storedRaceId]
    const found = athletes.find(a => String(a.bib) === bibStr)
    if (found) return found
  }

  return null
}

/**
 * Get roster statistics
 * @param {string} raceId - Race ID (optional, uses selected race if not provided)
 * @returns {Object} Stats about the roster
 */
export function getRosterStats(raceId = null) {
  const roster = loadRoster(raceId)

  const stats = {
    total: roster.length,
    male: roster.filter(a => a.gender === 'M').length,
    female: roster.filter(a => a.gender === 'F').length,
    teams: [...new Set(roster.map(a => a.team).filter(Boolean))],
    grades: [...new Set(roster.map(a => a.grade).filter(Boolean))].sort((a, b) => a - b),
    divisions: [...new Set(roster.map(a => a.division).filter(Boolean))]
  }

  return stats
}

/**
 * Check if a race has a roster loaded
 * @param {string} raceId - Race ID
 * @returns {boolean}
 */
export function hasRoster(raceId) {
  const rosters = loadLocal(STORAGE_KEYS.ROSTERS) || {}
  return rosters[raceId] && rosters[raceId].length > 0
}

/**
 * Save CSV column mapping for reuse
 * @param {string} name - Mapping name
 * @param {Object} mapping - Column mapping object
 */
export function saveCsvMapping(name, mapping) {
  const mappings = loadLocal(STORAGE_KEYS.CSV_MAPPINGS) || {}
  mappings[name] = { ...mapping, savedAt: Date.now() }
  saveLocal(STORAGE_KEYS.CSV_MAPPINGS, mappings)
}

/**
 * Load saved CSV column mappings
 * @returns {Object} Saved mappings
 */
export function loadCsvMappings() {
  return loadLocal(STORAGE_KEYS.CSV_MAPPINGS) || {}
}

/**
 * Delete a saved CSV mapping
 * @param {string} name - Mapping name
 */
export function deleteCsvMapping(name) {
  const mappings = loadLocal(STORAGE_KEYS.CSV_MAPPINGS) || {}
  delete mappings[name]
  saveLocal(STORAGE_KEYS.CSV_MAPPINGS, mappings)
}

// ============================================
// Race-specific functions
// ============================================

/**
 * Get all races - CLOUD FIRST
 * Returns races from cloud cache, falls back to localStorage
 * @returns {Array} Array of race objects
 */
export function getRaces() {
  // Cloud-first: prefer cloud cache
  return getCloudRaces()
}

/**
 * Save all races
 * @param {Array} races - Array of race objects
 */
export function saveRaces(races) {
  saveLocal(STORAGE_KEYS.RACES, races)
}

/**
 * Create or update a race
 * @param {Object} race - Race object
 * @returns {Object} The saved race (with id if new)
 */
export function saveRace(race) {
  const races = loadLocal(STORAGE_KEYS.RACES) || []

  if (race.id) {
    // Update existing race
    const idx = races.findIndex(r => r.id === race.id)
    if (idx >= 0) {
      races[idx] = { ...races[idx], ...race, updatedAt: Date.now() }
      race = races[idx]
    }
  } else {
    // Create new race
    race.id = generateUUID()
    race.createdAt = Date.now()
    race.updatedAt = Date.now()
    race.status = race.status || 'draft'
    races.push(race)
  }

  // Save to localStorage
  saveLocal(STORAGE_KEYS.RACES, races)

  // Optimistic update: add to cloud cache immediately so UI shows it
  addToCloudRacesCache(race)

  // Queue for Firebase sync
  queueForSync('race_save', race)
  return race
}

/**
 * Delete a race and ALL its local data
 * @param {string} raceId - Race ID to delete
 */
export function deleteRace(raceId) {
  // Remove from races list
  const races = getRaces()
  const filtered = races.filter(r => r.id !== raceId)
  saveRaces(filtered)

  // Clear local data for this race
  localStorage.removeItem(getRaceKey(STORAGE_KEYS.TIMESTAMPS, raceId))
  localStorage.removeItem(getRaceKey(STORAGE_KEYS.BIBS, raceId))
  localStorage.removeItem(getRaceKey(STORAGE_KEYS.ROSTER, raceId))

  // Clear from race-specific rosters
  const rosters = loadLocal(STORAGE_KEYS.ROSTERS) || {}
  if (rosters[raceId]) {
    delete rosters[raceId]
    saveLocal(STORAGE_KEYS.ROSTERS, rosters)
  }

  // If this was the selected race, clear selection
  const selectedRaceId = loadLocal(STORAGE_KEYS.SELECTED_RACE)
  if (selectedRaceId === raceId) {
    localStorage.removeItem(STORAGE_KEYS.SELECTED_RACE)
  }

  // Queue Firebase deletion (will delete race + timestamps + bibEntries + athletes)
  queueForSync('race_delete', { id: raceId })
}

/**
 * Reset race results (clear timestamps and bibs, but keep roster and race)
 * @param {string} raceId - Race ID to reset
 */
export function resetRaceResults(raceId) {
  // Clear local timestamps and bibs
  localStorage.removeItem(getRaceKey(STORAGE_KEYS.TIMESTAMPS, raceId))
  localStorage.removeItem(getRaceKey(STORAGE_KEYS.BIBS, raceId))

  // Update race status to 'ready'
  updateRaceStatus(raceId, 'ready')

  // Queue for Firebase sync (clears cloud timestamps/bibs)
  queueForSync('race_reset_results', { id: raceId })
}

/**
 * Get a race by ID
 * @param {string} raceId - Race ID
 * @returns {Object|null} Race object or null
 */
export function getRaceById(raceId) {
  const races = getRaces()
  return races.find(r => r.id === raceId) || null
}

/**
 * Get the currently selected race
 * @returns {Object|null} Selected race or null
 */
export function getSelectedRace() {
  const raceId = loadLocal(STORAGE_KEYS.SELECTED_RACE)
  if (!raceId) return null
  return getRaceById(raceId)
}

/**
 * Set the currently selected race
 * @param {string} raceId - Race ID to select
 */
export function setSelectedRace(raceId) {
  saveLocal(STORAGE_KEYS.SELECTED_RACE, raceId)
}

/**
 * Clear the selected race
 */
export function clearSelectedRace() {
  localStorage.removeItem(STORAGE_KEYS.SELECTED_RACE)
}

/**
 * Update race status
 * @param {string} raceId - Race ID
 * @param {string} status - New status ('draft', 'ready', 'live', 'finished')
 */
export function updateRaceStatus(raceId, status) {
  const races = getRaces()
  const race = races.find(r => r.id === raceId)
  if (race) {
    race.status = status
    race.updatedAt = Date.now()
    saveRaces(races)
    queueForSync('race_status', { id: raceId, status })
  }
}

/**
 * Get age group preset definition
 * @param {string} presetId - Preset identifier
 * @returns {Object} Preset definition
 */
export function getAgeGroupPreset(presetId) {
  return AGE_GROUP_PRESETS[presetId] || AGE_GROUP_PRESETS.standard
}

/**
 * Get all age group presets
 * @returns {Object} All preset definitions
 */
export function getAllAgeGroupPresets() {
  return AGE_GROUP_PRESETS
}

/**
 * Determine age group for an athlete based on race preset
 * @param {Object} athlete - Athlete object (with age and/or grade)
 * @param {Object} race - Race object (with ageGroupPreset)
 * @returns {string} Age group name
 */
export function getAthleteAgeGroup(athlete, race) {
  if (!race || !race.ageGroupPreset) return 'Open'

  const preset = AGE_GROUP_PRESETS[race.ageGroupPreset]
  if (!preset) return 'Open'

  // For high school preset, match by grade
  if (race.ageGroupPreset === 'high_school' && athlete.grade) {
    const group = preset.groups.find(g => g.grade === athlete.grade)
    if (group) return group.name
  }

  // For age-based presets, match by age
  if (athlete.age) {
    const group = preset.groups.find(g => {
      const matchesMin = g.minAge === null || athlete.age >= g.minAge
      const matchesMax = g.maxAge === null || athlete.age <= g.maxAge
      return matchesMin && matchesMax
    })
    if (group) return group.name
  }

  return 'Open'
}

export { STORAGE_KEYS, AGE_GROUP_PRESETS }
